// AWS WAF JS integration SDK wrapper. The SDK script (URL from the WAF
// console's "Application integration" page for the shared Web ACL) runs a
// silent browser challenge and issues tokens that the API's WAF rules
// require on POST requests.
const challengeUrl: string = import.meta.env.VITE_WAF_CHALLENGE_URL ?? '';

type AwsWafIntegration = {
  getToken: () => Promise<string>;
};

declare global {
  interface Window {
    AwsWafIntegration?: AwsWafIntegration;
  }
}

let sdkPromise: Promise<AwsWafIntegration | null> | null = null;

const loadWafSdk = (): Promise<AwsWafIntegration | null> => {
  if (!challengeUrl || typeof window === 'undefined') {
    return Promise.resolve(null);
  }
  if (sdkPromise) {
    return sdkPromise;
  }
  // Assigned synchronously (before any await/microtask) so concurrent
  // same-tick callers all share this one in-flight load rather than each
  // injecting their own <script> tag.
  const pending: Promise<AwsWafIntegration | null> = new Promise<AwsWafIntegration | null>((resolve) => {
    if (window.AwsWafIntegration) {
      resolve(window.AwsWafIntegration);
      return;
    }
    const script = document.createElement('script');
    script.src = challengeUrl;
    script.onload = () => resolve(window.AwsWafIntegration ?? null);
    script.onerror = () => {
      // Remove the failed element so a retry (see below) injects a
      // fresh <script> rather than leaving a dead one, and its load/error
      // listeners, behind in the DOM.
      script.remove();
      resolve(null);
    };
    document.head.appendChild(script);
  }).then((sdk) => {
    // Only a successful load is memoised. A failed load (blocked script,
    // transient network blip, etc.) must not be cached forever — clear the
    // memo so the next getWafToken() call retries the injection instead of
    // being stuck returning null for the rest of the page's life.
    if (!sdk) {
      sdkPromise = null;
    }
    return sdk;
  });
  sdkPromise = pending;
  return pending;
};

// Resolves to a WAF token, or null when the SDK is unavailable (local dev
// with VITE_WAF_CHALLENGE_URL unset, or the script blocked). Requests sent
// without a token are challenged by WAF and fail — acceptable for non-browser
// contexts; real browsers on olympiq.co.uk always get one.
export const getWafToken = async (): Promise<string | null> => {
  const sdk = await loadWafSdk();
  if (!sdk) {
    return null;
  }
  try {
    return await sdk.getToken();
  } catch {
    return null;
  }
};

export default getWafToken;
